/**
 * A type for determining how to label the underlying `<input>` element. Either a `string` for the default option, or an object for more control.
 */
export type LabelProp = string | LabelMethod;

export type LabelMethod = Default | Hidden | For | LabelledBy | DescribedBy;

export type Default = {
  /**
   * Display a `<FormLabel>` component with the specified label.
   */
  type: "default";
  label: string;
};

export type Hidden = {
  /**
   * Display a specified label, visible only to screen readers.
   */
  type: "hidden";
  label: string;
};

export type For = {
  /**
   * You're using a `<label>` element to label the input; can only label interactive elements like `<input>` (https://developer.mozilla.org/en-US/docs/Web/HTML/Element/label).
   */
  type: "htmlFor";
  htmlForAttributeOfLabelElement: string;
};

export type LabelledBy = {
  /**
   * You're using a DOM element to label the input via that element's ID property, **and** the text content of that element is concise. See https://www.w3.org/TR/wai-aria/#aria-labelledby for more info.
   *
   * Unlike with other labelling methods, clicking on the labelling element will _not_ focus the corresponding input.
   */
  type: "aria-labelledby";
  idOfLabellingElement: string;
};

export type DescribedBy = {
  /**
   * You're using a DOM element to label the input via that element's ID property, **and** the text content of that element is too long to be a normal label. See https://www.w3.org/TR/wai-aria/#aria-labelledby for more info.
   */
  type: "aria-describedby";
  /**
   * @deprecated
   * Describing elements will not be picked up as an actual label, so use this as a last resort.
   */
  idOfDescribingElement: string;
};

/**
 *
 * @param labelProp
 * @returns a discriminated union used to determine how to connect the input element to its label.
 */
export function normalizeLabelProp(labelProp: LabelProp): LabelMethod {
  if (typeof labelProp === "string")
    return {
      type: "default",
      label: labelProp,
    };

  return labelProp;
}

type LabelAttribute =
  | {
      id: string;
    }
  | {
      "aria-labelledby": string;
    }
  | {
      "aria-describedby": string;
    }
  | {
      id: undefined;
    };

type RequiredAttribute =
  // For <input> elements
  | { required: boolean }
  // For non-interactive elements, e.g. <fieldset> or <div>
  | { "aria-required": boolean };

interface GetCorrectLabelAttributeArgs {
  labelProp: LabelProp;
  /**
   * The `id` attribute of either the input element or the labelling element, depending on the labelling method.
   */
  htmlId: string;
  /**
   * E.g. if the element to be labelled is a `<div>`, not an `<input>` or `<button>`.
   */
  elementIsNonInteractive: boolean;
  fieldIsRequired: boolean;
}
export function getCorrectHtmlAttributesForInput({
  labelProp,
  htmlId,
  elementIsNonInteractive,
  fieldIsRequired,
}: GetCorrectLabelAttributeArgs): LabelAttribute & RequiredAttribute {
  const requiredAttribute: RequiredAttribute = elementIsNonInteractive
    ? { "aria-required": fieldIsRequired }
    : { required: fieldIsRequired };
  if (typeof labelProp === "string") {
    if (elementIsNonInteractive)
      return {
        ...requiredAttribute,
        "aria-labelledby": htmlId,
      };
    return {
      ...requiredAttribute,
      id: htmlId,
    };
  }
  switch (labelProp.type) {
    case "default":
    case "hidden":
      if (elementIsNonInteractive)
        return {
          ...requiredAttribute,
          "aria-labelledby": htmlId,
        };
      return {
        ...requiredAttribute,
        id: htmlId,
      };
    case "aria-labelledby":
      return {
        ...requiredAttribute,
        "aria-labelledby": labelProp.idOfLabellingElement,
      };
    case "aria-describedby":
      return {
        ...requiredAttribute,
        "aria-describedby": labelProp.idOfDescribingElement,
      };
    case "htmlFor":
      return {
        ...requiredAttribute,
        id: labelProp.htmlForAttributeOfLabelElement,
      };
  }
}
